import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getRequest } from 'api/apiClient';
import axios from 'axios';
import dayjs from 'dayjs';

interface IntegrationState {
    isMsiIntegrated: boolean;
    isGuestPassIntegrated: boolean;
    isTokenValid: boolean;
    loading: boolean;
    error: string | null;
}

const initialState: IntegrationState = {
    isMsiIntegrated: false,
    isGuestPassIntegrated: false,
    isTokenValid: false,
    loading: false,
    error: null,
};

export const fetchIntegrationStatus = createAsyncThunk<IntegrationState, { orgId: number }, { rejectValue: string }>(
    'integration/fetchIntegrationStatus',
    async ({ orgId }, { rejectWithValue }) => {
        try {
            const integrationData = await getRequest(`/orgs/${orgId}/integrationMsiAlta`);

            if (!integrationData.data || integrationData.data.length === 0) {
                return {
                    isMsiIntegrated: false,
                    isGuestPassIntegrated: false,
                    isTokenValid: false,
                    loading: false,
                    error: null,
                };
            }

            const initialData = integrationData.data[0];
            const { msiAltaOrgId, msiAltaToken, msiAltaTokenExpUtc, guestPassEnabled } = initialData;

            const hasOrgId = !!msiAltaOrgId;
            const hasToken = !!msiAltaToken;
            const tokenIsValid = !msiAltaTokenExpUtc || dayjs.utc(msiAltaTokenExpUtc).isAfter(dayjs.utc());
            const guestPassEnabledCondition = guestPassEnabled === 1;
            const msiAltaApiHost = integrationData.data[0].msiAltaApiHost || 'https://api.openpath.com';

            let isTokenValid = false;
            if (hasOrgId && hasToken && tokenIsValid) {
                const response = await axios.post(
                    `${msiAltaApiHost}/auth/accessTokens/${msiAltaToken}/validate`
                );
                isTokenValid = response.data.data.isValid;
            }

            return {
                isMsiIntegrated: hasOrgId && hasToken && tokenIsValid,
                isGuestPassIntegrated: hasOrgId && hasToken && tokenIsValid && guestPassEnabledCondition,
                isTokenValid,
                loading: false,
                error: null,
            };
        } catch (error) {
            return rejectWithValue('Failed to fetch integration status');
        }
    });

const integrationSlice = createSlice({
    name: 'integration',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchIntegrationStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchIntegrationStatus.fulfilled, (state, action) => {
                state.isMsiIntegrated = action.payload.isMsiIntegrated;
                state.isGuestPassIntegrated = action.payload.isGuestPassIntegrated;
                state.isTokenValid = action.payload.isTokenValid;
                state.loading = false;
                state.error = null;
            })
            .addCase(fetchIntegrationStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Unknown error';
            });
    },
});

export default integrationSlice.reducer;
